import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { calendarPath, CALENDAR_VIEW_FROM_SLUG } from "../../../../routes/slugs";
import { useWidth } from "../../../../hooks/useWidth";
import { activityTracker } from "../../../../utils/activityTracker";
import type { Psychologist } from "../../../../types/psychologist";
import WeeklyPsychologistCalendar from "../../../../components/psychologist/WeeklyPsychologistCalendar";
import { useAppShell } from "../../../../contexts/AppShellContext";
import { useCalendarSession } from "../../../../components/psychologist/calendar/hooks/useCalendarSession";
import { useCalendarEditMode } from "../../../../components/psychologist/calendar/hooks/useCalendarEditMode";
import { useCalendarTour } from "./hooks/useCalendarTour";
import { CalendarOnboarding } from "./components/CalendarOnboarding";
import { CalendarConfigDropdown } from "../../../../components/psychologist/calendar/CalendarConfigDropdown";
import { CalendarSchedulingBanner } from "../../../../components/psychologist/calendar/CalendarSchedulingBanner";
import { HolidayAgendaNotice } from "../../../../components/psychologist/calendar/HolidayAgendaNotice";
import { CalendarUnsavedModal } from "../../../../components/psychologist/calendar/CalendarUnsavedModal";
import { CalendarHelpModal } from "./components/CalendarHelpModal";
import { ViewSelector, type CalendarView } from "./components/ViewSelector";
import { PatientAgendaSearch } from "../../../../components/psychologist/calendar/PatientAgendaSearch";
import { DayView } from "./views/DayView";
import { MonthView } from "./views/MonthView";
import LargeLoading from "../../../../components/common/LargeLoading";
import { GoogleMeetButton } from "../../../../components/common/GoogleMeetButton";
import { useAcuityMigration } from "../../../../contexts/AcuityMigrationContext";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { PatientDetailModal } from "../patients/components/PatientDetailModal";
import type { Patient } from "../patients/components/PatientTable";
import { BA_TIMEZONE } from "../../../../utils/timezone";
import { useMissingNextSession } from "./hooks/useMissingNextSession";
import { useRescheduleAvailabilityBanner } from "./hooks/useRescheduleAvailabilityBanner";
import CalendarBanner from "../../../../components/psychologist/calendar/CalendarBanner";
import MandatoryScheduleCtaModal from "../../../../components/psychologist/calendar/MandatoryScheduleCtaModal";
import NzWelcomeModal from "../../../../components/psychologist/calendar/NzWelcomeModal";
import TimezoneAgendaUpdatedModal from "../../../../components/psychologist/calendar/TimezoneAgendaUpdatedModal";
import {
  getPendingTimezoneAgendaChange,
  dismissTimezoneAgendaBanner,
} from "../../../../utils/timezoneAgendaNotice";
import { MissingNextSessionModal } from "../sessions/components/MissingNextSessionModal";
import { useAuth } from "../../../../contexts/AuthContext";
import SessionDetailsModal from "../../../../components/psychologist/calendar/SessionDetailsModal";
import type { SessionWithPsychologist, SessionStatus } from "../../../../components/psychologist/calendar/data";
import { isoToZoneParts } from "../../../../components/psychologist/calendar/timeZoneHelpers";
import { SESSION_APPOINTMENT_TYPES } from "../../../../hooks/api/config";
import { useNoAgendamientoModal } from "../../../../components/psychologist/patients/no-agendamiento/useNoAgendamientoModal";
import { NoAgendamientoModal } from "../../../../components/psychologist/patients/no-agendamiento/NoAgendamientoModal";
import { sessionsGrade } from "../../../../components/psychologist/patients/no-agendamiento/noAgendamientoFormSchema";
import {
  timezoneToCountryCode,
  countryCodeToName,
} from "../../../../utils/timezoneToCountry";
import CountryFlag from "../../../../components/common/CountryFlag";
import { NEW_STATUS_ID } from "../../../../utils/psychologistStatus";

interface CalendarTabProps {
  psychologist: Psychologist | null;
  onTourRunningChange?: (isRunning: boolean) => void;
  preloadProcessId?: string;
  onPreloadConsumed?: () => void;
  autoOpenPatientEmail?: string;
  autoOpenDate?: string;
  onAutoOpenConsumed?: () => void;
  // Deep-link compartible: abrir un modo de configuración de horarios directo
  initialConfig?: "recurring" | "specific-day" | "block";
  initialConfigDate?: string;
  /** Solo con initialConfig="block": motivo precargado en el panel de bloqueo. */
  initialConfigReason?: string;
  onConfigConsumed?: () => void;
  // Deep-link compartible: abrir el modal de detalle de una sesión por id
  autoOpenSessionId?: string;
  autoOpenSessionView?: string;
  onAutoOpenSessionConsumed?: () => void;
  // Deep-link compartible: abrir el modal de agendar (paciente preseleccionado vía preloadProcessId)
  autoOpenSchedule?: boolean;
  // Refleja el estado de agendar en la URL para poder compartir el link (solo al estar agendando)
  onBookingStateChange?: (processId: string) => void;
  // Se llama al terminar el agendamiento (confirmado o cancelado) para limpiar la URL
  onBookingEnded?: () => void;
}

export const CalendarTab = ({
  psychologist,
  onTourRunningChange,
  preloadProcessId,
  onPreloadConsumed,
  autoOpenPatientEmail,
  autoOpenDate,
  onAutoOpenConsumed,
  initialConfig,
  initialConfigDate,
  initialConfigReason,
  onConfigConsumed,
  autoOpenSessionId,
  autoOpenSessionView,
  onAutoOpenSessionConsumed,
  autoOpenSchedule,
  onBookingStateChange,
  onBookingEnded,
}: CalendarTabProps) => {
  const { setCalendarDirty } = useAppShell();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { view: viewSlug } = useParams<{ view?: string }>();
  const { user } = useAuth();
  const noAgendamientoModal = useNoAgendamientoModal();

  // Huso horario real de la psicóloga (fallback a Argentina)
  const therapistTz = psychologist?.timezone || BA_TIMEZONE;

  // ─── View routing ────────────────────────────────────────────────────────
  // Fuente de verdad: el slug de path (/calendar/semanal|mensual|diaria). El query param
  // legacy `?view=` se mantiene funcionando (redirect abajo) pero NUNCA se borra de la query
  // string una vez migrado: `CalendarPage.tsx` reutiliza esa misma clave `view` con un
  // significado totalmente distinto (sub-modo del modal de sesión, `details`/`reschedule`,
  // acompaña a `?session=`). Tocarla acá rompería ese deep-link.
  const VIEW_STORAGE_KEY = "psi_calendar_view";

  // "week" también cuenta como legacy explícito (ej. ?view=week debe ganarle a un
  // localStorage con "month"), pero cualquier otro valor (ej. "reschedule", del sub-modo
  // del modal de sesión) se trata como ausente — no es un valor de vista de Agenda.
  const legacyView = searchParams.get("view");
  const legacyViewValid =
    legacyView === "month" || legacyView === "day" || legacyView === "week" ? legacyView : null;

  const currentView: CalendarView = viewSlug
    ? (CALENDAR_VIEW_FROM_SLUG[viewSlug] ?? "week")
    : (legacyViewValid ?? "week");

  useEffect(() => {
    if (viewSlug) {
      // Ya estamos en un slug real — persistir para la próxima vez que entre por /calendar bare.
      const internal = CALENDAR_VIEW_FROM_SLUG[viewSlug];
      if (internal) { try { localStorage.setItem(VIEW_STORAGE_KEY, internal); } catch { /* noop */ } }
      return;
    }
    if (legacyViewValid) {
      // Link viejo ?view=month|day sin slug en el path → migrar, preservando el resto de la
      // query tal cual (incluida la propia `view`, ver nota arriba).
      try { localStorage.setItem(VIEW_STORAGE_KEY, legacyViewValid); } catch { /* noop */ }
      const qs = searchParams.toString();
      navigate(`${calendarPath(legacyViewValid)}${qs ? `?${qs}` : ""}`, { replace: true });
      return;
    }
    // Bare /calendar sin ningún indicio explícito: restaurar de localStorage.
    const saved = (() => {
      try { return localStorage.getItem(VIEW_STORAGE_KEY); } catch { return null; }
    })();
    if (saved === "month" || saved === "day") {
      const next = new URLSearchParams(searchParams);
      if (saved === "day") {
        const todayISO = new Date().toLocaleDateString("en-CA", { timeZone: therapistTz });
        next.set("date", todayISO);
      }
      const q = next.toString();
      navigate(`${calendarPath(saved)}${q ? `?${q}` : ""}`, { replace: true });
    }
    // "week" is the default — no need to redirect if not saved
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewSlug]);

  // Show informational modal once per session when arriving via the "Sesiones" navbar link
  useEffect(() => {
    const fromSesiones = searchParams.get("from") === "sesiones";
    if (!fromSesiones) return;
    const alreadyShown = (() => {
      try { return sessionStorage.getItem("psi_sesiones_redirect_shown") === "true"; } catch { return false; }
    })();
    if (!alreadyShown) {
      setSesionesRedirectModalOpen(true);
    }
    // Clean ?from=sesiones from the URL regardless
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("from");
        return next;
      },
      { replace: true },
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const closeSesionesRedirectModal = useCallback(() => {
    setSesionesRedirectModalOpen(false);
    try { sessionStorage.setItem("psi_sesiones_redirect_shown", "true"); } catch { /* noop */ }
  }, []);

  const handleViewChange = useCallback(
    (newView: CalendarView) => {
      try { localStorage.setItem(VIEW_STORAGE_KEY, newView); } catch { /* noop */ }
      // Nunca tocar la clave `view` de la query acá (ver nota en "View routing" arriba) —
      // solo cambia el path.
      const next = new URLSearchParams(searchParams);
      if (newView !== "day") next.delete("date");
      if (newView === "day") {
        const todayISO = new Date().toLocaleDateString("en-CA", {
          timeZone: therapistTz,
        });
        next.set("date", todayISO);
      }
      const q = next.toString();
      navigate(`${calendarPath(newView)}${q ? `?${q}` : ""}`, { replace: true });
    },
    [navigate, searchParams, therapistTz],
  );

  // ─── Month navigation state (for MonthView) ───────────────────────────────
  const [currentMonthDate, setCurrentMonthDate] = useState<Date>(
    () => new Date(),
  );

  const goToPrevMonth = () => {
    setCurrentMonthDate(
      (d) => new Date(d.getFullYear(), d.getMonth() - 1, 1),
    );
  };
  const goToNextMonth = () => {
    setCurrentMonthDate(
      (d) => new Date(d.getFullYear(), d.getMonth() + 1, 1),
    );
  };
  const goToTodayMonth = () => setCurrentMonthDate(new Date());

  // ─── DayView patient modal ────────────────────────────────────────────────
  const [dayViewPatient, setDayViewPatient] = useState<Patient | null>(null);

  const handleDayViewPatientClick = useCallback(
    (
      patientId: string,
      processId?: string,
      patientName?: string,
      therapyType?: string,
    ) => {
      setDayViewPatient({
        id: patientId,
        processId: processId || patientId,
        name: patientName || "",
        service: therapyType,
        totalSessions: 0,
      });
    },
    [],
  );

  // ─── Psychologist normalization ─────────────────────────────────────────
  const calendarPsychologistProp = useMemo(
    () => ({
      id: psychologist?.id ?? "",
      specialties:
        psychologist?.specialties && psychologist.specialties.length > 0
          ? psychologist.specialties
          : ["anxiety", "stress"],
      statusId: psychologist?.status?.id,
    }),
    [psychologist?.id, psychologist?.specialties, psychologist?.status?.id],
  );

  // ─── Shared state ────────────────────────────────────────────────────────
  const [openScheduleModalTick, setOpenScheduleModalTick] = useState(0);
  const [cancelSchedulingTick, setCancelSchedulingTick] = useState(0);
  const [configDropdownOpen, setConfigDropdownOpen] = useState(false);
  const configDropdownRef = useRef<HTMLDivElement>(null);
  const [helpModalOpen, setHelpModalOpen] = useState(false);
  const [sesionesRedirectModalOpen, setSesionesRedirectModalOpen] = useState(false);
  const [timezoneChangeInfo, setTimezoneChangeInfo] = useState(() => getPendingTimezoneAgendaChange());
  const handleCloseTimezoneModal = useCallback(() => {
    dismissTimezoneAgendaBanner();
    setTimezoneChangeInfo(null);
  }, []);
  const [calendarDetailPatient, setCalendarDetailPatient] = useState<Patient | null>(null);
  const openRescheduleInfoFnRef = useRef<(() => void) | null>(null);
  const openRescheduleSummaryFnRef = useRef<(() => void) | null>(null);
  const todayFnRef = useRef<(() => void) | null>(null);
  const dayViewTodayRef = useRef<(() => void) | null>(null);
  const [isSchedulingMode, setIsSchedulingMode] = useState(false);
  const [schedulingPatientName, setSchedulingPatientName] = useState<
    string | undefined
  >(undefined);
  // Detecta la transición agendar→no-agendar para limpiar la URL solo al terminar
  // (no en el `false` inicial del mount, que pisaría el deep-link ?book=1).
  const wasSchedulingRef = useRef(false);
  const [isRescheduleMode, setIsRescheduleMode] = useState(false);
  const [reschedulingSessionInfo, setReschedulingSessionInfo] = useState<{
    patientName: string;
    date: string;
  } | null>(null);

  const handleRescheduleModeChange = useCallback(
    (
      isReschedule: boolean,
      session: { patient: { name?: string; email: string }; date: string } | null,
    ) => {
      setIsRescheduleMode(isReschedule);
      if (isReschedule && session) {
        setReschedulingSessionInfo({
          patientName: session.patient.name || session.patient.email,
          date: session.date.split("-").reverse().join("/"),
        });
      } else {
        setReschedulingSessionInfo(null);
      }
    },
    [],
  );

  // Nota: el redirect NZ (expulsión a quierosumarme / restricción a solo Agenda) ahora vive
  // centralizado en AppShell.tsx, que corre en toda la app y no depende de haber visitado
  // /calendar ni de `hasWorkSchedules` local a este componente.

  // ─── Feature hooks ───────────────────────────────────────────────────────
  const { size } = useWidth();
  const isMobile = size === "s";

  // ─── Missing next session (only relevant in day view) ───────────────────
  const {
    patientsMissingNext,
    patientsHandled,
    missingNextModalOpen,
    openMissingNextModal,
    dismissMissingNextModal,
    resetForViewEntry,
  } = useMissingNextSession(psychologist?.id);

  // ─── Banner 1 (reprogramación) — visible en las 3 vistas de Agenda ───────
  const { rescheduleMinutes, rescheduleSlotsNextWeek, bannerShouldShow: rescheduleBannerShouldShow } =
    useRescheduleAvailabilityBanner(psychologist?.id);

  const handleOpenRescheduleSummary = useCallback(() => {
    if (currentView === "week") {
      openRescheduleSummaryFnRef.current?.();
    } else {
      handleViewChange("week");
      setTimeout(() => openRescheduleSummaryFnRef.current?.(), 200);
    }
  }, [currentView, handleViewChange]);

  // Cuando el usuario entra a Day View desde otra vista, resetear el estado
  // de cierre del modal para replicar el comportamiento de /sessions donde
  // cada navegación a la ruta abre el modal de nuevo si hay pacientes.
  const prevViewRef = useRef<string>(currentView);
  useEffect(() => {
    const prev = prevViewRef.current;
    prevViewRef.current = currentView;
    if (currentView === "day" && prev !== "day") {
      resetForViewEntry();
    }
  }, [currentView, resetForViewEntry]);

  // Close missing-next modal on Escape
  useEffect(() => {
    if (!missingNextModalOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") dismissMissingNextModal();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [missingNextModalOpen, dismissMissingNextModal]);

  const handleMissingNextSchedule = useCallback(
    (processId?: string) => {
      const next = new URLSearchParams(searchParams);
      next.delete("date");
      if (processId) next.set("process", processId);
      const q = next.toString();
      navigate(`${calendarPath("week")}${q ? `?${q}` : ""}`, { replace: true });
      setTimeout(() => {
        if (!processId) setOpenScheduleModalTick(Date.now());
      }, 150);
    },
    [navigate, searchParams],
  );

  const handleMissingNextNoSchedule = useCallback(
    (patient: { patientId?: string; patientName: string; email?: string; processId?: string; totalSessions?: number }) => {
      const psyEmail = user?.email ?? "";
      const total = Number.isFinite(patient.totalSessions) ? patient.totalSessions! : 0;
      noAgendamientoModal.open({
        processId: patient.processId || '',
        patientEmail: patient.email || '',
        patientName: patient.patientName || '',
        psychologistEmail: psyEmail,
        totalSessionsGrade: sessionsGrade(total),
      });
    },
    [noAgendamientoModal, user?.email],
  );

  const session = useCalendarSession();

  // ─── Month view session popup ─────────────────────────────────────────────
  const handleMonthSessionClick = useCallback(
    (sessionLite: import("../../../../types/session-lite").SessionLite) => {
      const rawDate = sessionLite.date ?? '';
      // Mirror exactly what WeeklyPsychologistCalendar does: use isoToZoneParts
      // with the psychologist's real timezone so the result matches the calendar
      // grid regardless of the browser's local timezone.
      // origISO = raw API string (same as data.ts), so SessionDetailsModal can
      // recompute zone-aware parts if needed.
      const parts = isoToZoneParts(rawDate, therapistTz);

      const converted: SessionWithPsychologist = {
        id: sessionLite.id,
        psychologistId: psychologist?.id ?? '',
        date: parts.date,
        time: parts.time,
        modality: '',
        status: sessionLite.status as SessionStatus,
        patient: {
          name: sessionLite.patientName,
          email: '',
          timezone: sessionLite.patientTimezone,
        },
        specialty: sessionLite.therapyType,
        process_id: sessionLite.process,
        // Pareja/Familia duran 90: con 60 fijo, "Reportar ausencia" desaparecía a la hora.
        duration: SESSION_APPOINTMENT_TYPES.find((t) => t.name === sessionLite.therapyType)?.duration ?? 60,
        origISO: rawDate,
      };
      session.setSelectedSession(converted);
      session.setIsSessionModalOpen(true);
    },
    [psychologist?.id, session, therapistTz],
  );

  const editMode = useCalendarEditMode({ setCalendarDirty });
  const acuityMigration = useAcuityMigration();
  const showAcuityReview =
    acuityMigration.welcomeDismissed &&
    acuityMigration.isPendingReview &&
    editMode.calendarEditMode;

  // Nunca se muestra el banner de reprogramación a psicólogas con estado NEW (recién
  // creadas, aún sin horarios cargados) — no aplica exigirles horarios de reprogramación
  // en esa etapa.
  const isNewPsychologist = psychologist?.status?.id === NEW_STATUS_ID;
  // NZ recién creada, en estado "new", todavía sin horarios reales — mismo criterio que
  // usa AppShell para `restrictToAgenda`. Para este grupo, el paso simulado de carga de
  // horarios del tour corta la guía ahí (no sigue al tour de agendamiento) y en su lugar
  // se les pide configurar su disponibilidad real.
  const isMandatoryScheduleOnboarding = psychologist?.ranking_category === 'NZ' && isNewPsychologist;

  const tour = useCalendarTour({
    calendarEditMode: editMode.calendarEditMode,
    setCalendarEditMode: editMode.setCalendarEditMode,
    specificDayDate: editMode.specificDayDate,
    setSpecificDayDate: editMode.setSpecificDayDate,
    openBlockModalTick: editMode.openBlockModalTick,
    setOpenBlockModalTick: editMode.setOpenBlockModalTick,
    setIsExitingEdit: editMode.setIsExitingEdit,
    setConfigDropdownOpen,
    setOpenScheduleModalTick,
    setCancelSchedulingTick,
    scheduleHasChanges: editMode.scheduleHasChanges,
    setScheduleHasChanges: editMode.setScheduleHasChanges,
    setShowUnsavedModal: editMode.setShowUnsavedModal,
    onTourRunningChange,
    isMandatoryScheduleOnboarding,
  });

  const rescheduleBanner = !isNewPsychologist &&
    !tour.runTour &&
    !tour.runBookingTour &&
    localStorage.getItem('psi-calendar-tour-done') === '1' && (
    <CalendarBanner
      rescheduleMinutes={rescheduleMinutes}
      rescheduleSlotsNextWeek={rescheduleSlotsNextWeek}
      weekKey={currentView}
      visible={rescheduleBannerShouldShow}
      onConfigureReschedule={handleOpenRescheduleSummary}
    />
  );

  // ─── "Salir sin guardar" (composition point: tour + editMode) ────────────
  const handleDiscard = () => {
    editMode.setShowUnsavedModal(false);
    editMode.setScheduleHasChanges(false);
    editMode.setSpecificDayHasChanges(false);
    editMode.setBlockHasChanges(false);

    if (tour.shouldStartBookingTour) {
      tour.startBookingAfterDiscard();
    } else if (editMode.pendingCloseCallback) {
      editMode.pendingCloseCallback();
      editMode.setPendingCloseCallback(null);
    } else {
      tour.handleTourDiscardExit();
      editMode.setCalendarEditMode(false);
      editMode.setOpenBlockModalTick(false);
      editMode.setSpecificDayDate(null);
    }
  };

  // ─── Config dropdown actions ──────────────────────────────────────────────
  const handleStartRecurrent = () => {
    if (editMode.isTogglingEdit) return;
    // Métricas de Actividad (funnel disponibilidad): empieza a editar sus horarios.
    activityTracker.trackAction("availability_edit_started");
    editMode.setIsTogglingEdit(true);
    window.setTimeout(() => {
      editMode.setCalendarEditMode(true);
      editMode.setIsTogglingEdit(false);
    }, 450);
  };

  const handleStartSpecificDay = (date = "") => {
    editMode.setSpecificDayDate(date);
    if (!editMode.calendarEditMode) {
      activityTracker.trackAction("availability_edit_started");
      editMode.setIsTogglingEdit(true);
      window.setTimeout(() => {
        editMode.setCalendarEditMode(true);
        editMode.setIsTogglingEdit(false);
      }, 450);
    }
  };

  // Fecha/motivo precargados en el panel de bloqueo — solo cuando se entra por deep-link
  // (ej. "Bloquear mi agenda" desde el aviso de feriado de Comunidad). Abrirlo a mano
  // desde el menú de configuración lo deja vacío, como siempre.
  const [blockPrefill, setBlockPrefill] = useState<{ date?: string; reason?: string } | null>(null);

  const handleStartBlock = () => {
    activityTracker.trackAction("availability_edit_started");
    setBlockPrefill(null);
    editMode.setSpecificDayDate(null);
    editMode.setOpenBlockModalTick(true);
    setTimeout(() => {
      if (!editMode.calendarEditMode) editMode.setCalendarEditMode(true);
    }, 450);
  };

  // ─── Deep-link: abrir un modo de configuración desde la URL (una sola vez) ──
  const configConsumedRef = useRef(false);
  useEffect(() => {
    if (configConsumedRef.current || !initialConfig) return;
    configConsumedRef.current = true;
    if (initialConfig === "recurring") handleStartRecurrent();
    else if (initialConfig === "specific-day") handleStartSpecificDay(initialConfigDate ?? "");
    else if (initialConfig === "block") {
      handleStartBlock();
      if (initialConfigDate || initialConfigReason) {
        setBlockPrefill({ date: initialConfigDate, reason: initialConfigReason });
      }
    }
    onConfigConsumed?.();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialConfig, initialConfigDate]);

  // ─── Month label helper ───────────────────────────────────────────────────
  const monthLabel = useMemo(() => {
    const name = currentMonthDate.toLocaleDateString("es-ES", {
      month: "long",
    });
    return `${name.charAt(0).toUpperCase()}${name.slice(1)} ${currentMonthDate.getFullYear()}`;
  }, [currentMonthDate]);

  // ─── Handle Agendar sesión from non-week views ────────────────────────────
  const handleScheduleFromOtherView = useCallback(() => {
    handleViewChange("week");
    setTimeout(() => setOpenScheduleModalTick(Date.now()), 150);
  }, [handleViewChange]);

  // ─── View selector node (shared between week's CalendarHeader and other views) ──
  const viewSelectorNode = (
    <ViewSelector currentView={currentView} onViewChange={handleViewChange} />
  );

  // ─── Month / Day view header ──────────────────────────────────────────────
  const renderPeriodHeader = () => {
    const isMonth = currentView === "month";

    const onToday = () => {
      if (isMonth) {
        goToTodayMonth();
      } else {
        // Day view: call the hook's onToday directly via ref to avoid
        // the navTargetRef hop mechanism which flashes intermediate days.
        dayViewTodayRef.current?.();
      }
    };

    // Timezone label — same logic as CalendarHeader component
    const detectedTimezone = therapistTz;
    const gmtOffset =
      new Date()
        .toLocaleTimeString("en-US", {
          timeZone: detectedTimezone,
          timeZoneName: "shortOffset",
        })
        .match(/GMT[+-]\d+/)?.[0] ?? "";
    const timezoneLabel = `${gmtOffset} - ${detectedTimezone}`;

    // Bandera + país del huso — misma lógica que el corner cell del TimeGrid (main)
    const tzIso2 = timezoneToCountryCode(detectedTimezone);
    const tzCountryName =
      (tzIso2 ? countryCodeToName(tzIso2) : null)?.toUpperCase() ?? "ARGENTINA";

    // When editing actions are triggered from month/day, switch to week first
    const switchToWeekThen = (action: () => void) => {
      handleViewChange("week");
      setTimeout(action, 200);
    };

    return (
      <>
        {/* Aviso de feriados próximos (14 días) — mismo en las 3 vistas y en la app mobile. */}
        <HolidayAgendaNotice className={isMobile ? "px-4 pt-2.5" : "px-8 pt-5"} />
        {/* ── Row 1: CalendarConfigDropdown (idéntico al de WeekView) ── */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: isMobile ? "0.625rem 1rem" : "1.25rem 2rem",
            flexShrink: 0,
            gap: "0.5rem",
          }}
        >
          {/* Left slot: "Sin próxima sesión" button (day view only) */}
          {!isMonth && patientsMissingNext.length + patientsHandled.length > 0 && (
            <button
              type="button"
              data-testid="btn-sin-proxima-sesion"
              onClick={openMissingNextModal}
              title="Ver pacientes sin próxima sesión"
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: isMobile ? 0 : "0.375rem",
                padding: isMobile ? "0" : "0.375rem 0.875rem",
                width: isMobile ? "40px" : undefined,
                borderRadius: "9999px",
                border: patientsMissingNext.length > 0
                  ? "2px solid var(--secundario-naranja, #f57e2e)"
                  : "2px solid #d1d5db",
                background: "transparent",
                color: patientsMissingNext.length > 0
                  ? "var(--secundario-naranja, #f57e2e)"
                  : "#9ca3af",
                fontSize: isMobile ? "0.875rem" : "0.8125rem",
                fontWeight: 600,
                fontFamily: "Poppins, sans-serif",
                cursor: "pointer",
                whiteSpace: "nowrap",
                flexShrink: 0,
                height: "40px",
              }}
            >
              {!isMobile && "Sin próxima sesión"}
              <span
                data-testid="badge-sin-proxima"
                style={{
                  background: patientsMissingNext.length > 0
                    ? "var(--secundario-naranja, #f57e2e)"
                    : "#d1d5db",
                  color: "#fff",
                  borderRadius: "9999px",
                  fontSize: "0.6875rem",
                  fontWeight: 700,
                  minWidth: "1.25rem",
                  height: "1.25rem",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "0 0.25rem",
                }}
              >
                {patientsMissingNext.length + patientsHandled.length}
              </span>
            </button>
          )}
          <div
            style={{
              display: "flex",
              gap: "0.5rem",
              alignItems: "center",
              justifyContent: "flex-end",
              flex: 1,
            }}
          >
            <CalendarConfigDropdown
              calendarEditMode={editMode.calendarEditMode}
              isSchedulingMode={isSchedulingMode}
              isRescheduleMode={isRescheduleMode}
              isTogglingEdit={editMode.isTogglingEdit}
              configDropdownOpen={configDropdownOpen}
              setConfigDropdownOpen={setConfigDropdownOpen}
              configDropdownRef={configDropdownRef}
              runTour={tour.runTour}
              onReplayTour={() => switchToWeekThen(tour.replayTour)}
              onScheduleSession={handleScheduleFromOtherView}
              onStartRecurrent={() => switchToWeekThen(handleStartRecurrent)}
              onStartSpecificDay={() => switchToWeekThen(handleStartSpecificDay)}
              onStartBlock={() => switchToWeekThen(handleStartBlock)}
              onOpenHelp={() => setHelpModalOpen(true)}
              onOpenRescheduleInfo={() => openRescheduleInfoFnRef.current?.()}
              onToday={onToday}
            />
          </div>
        </div>

        {/* ── Fila 2 mobile: ViewSelector (izq) + Hoy (der) ── */}
        {isMobile && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: "0.375rem",
              padding: "0 1rem 0.625rem",
              borderBottom: "1px solid #f1f5f9",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                gap: "0.5rem",
                // En Mes, ViewSelector + flechas + label de mes + búsqueda no siempre entran
                // en una sola línea en pantallas angostas (375px) — wrap evita que la lupa
                // quede empujada fuera del viewport (ver e2e "sin scroll horizontal").
                flexWrap: "wrap",
                rowGap: "0.375rem",
              }}
            >
            {/* ViewSelector siempre visible en mobile */}
            {viewSelectorNode}

            {/* Derecha: flechas ‹ mes › (solo Mes) + búsqueda de pacientes (siempre, misma
                posición que en semanal/desktop) */}
            <div style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem", justifyContent: "flex-end" }}>
              {isMonth && (
                <div style={{ display: "inline-flex", alignItems: "center", gap: "0.25rem" }}>
                  <button
                    type="button"
                    aria-label="Mes anterior"
                    onClick={goToPrevMonth}
                    style={{
                      background: "transparent",
                      border: 0,
                      color: "#6D57BC",
                      fontSize: "2rem",
                      width: "44px",
                      height: "44px",
                      display: "grid",
                      placeItems: "center",
                      alignContent: "center",
                      lineHeight: 1,
                      cursor: "pointer",
                    }}
                  >
                    ‹
                  </button>
                  <span
                    style={{
                      color: "#6D57BC",
                      fontWeight: 700,
                      fontSize: "0.9375rem",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {monthLabel}
                  </span>
                  <button
                    type="button"
                    aria-label="Mes siguiente"
                    onClick={goToNextMonth}
                    style={{
                      background: "transparent",
                      border: 0,
                      color: "#6D57BC",
                      fontSize: "2rem",
                      width: "44px",
                      height: "44px",
                      display: "grid",
                      placeItems: "center",
                      alignContent: "center",
                      lineHeight: 1,
                      cursor: "pointer",
                    }}
                  >
                    ›
                  </button>
                </div>
              )}
              {!editMode.calendarEditMode && !isSchedulingMode && !isRescheduleMode && (
                <PatientAgendaSearch />
              )}
            </div>

            </div>
          </div>
        )}

        {/* ── Row 2: CalendarHeader-style — [Hoy][ViewSelector][nav][timezone] ── */}
        {!isMobile && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              padding: "0.5rem 2rem",
              gap: "0.75rem",
              borderBottom: "1px solid #f1f5f9",
            }}
          >
            {/* Left: Hoy + ViewSelector */}
            <div
              style={{
                minWidth: "15.625rem",
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: "0.375rem",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <button type="button" className="btn-outline" onClick={onToday}>
                  Hoy
                </button>
                {viewSelectorNode}
              </div>
            </div>

            {/* Center: month navigation (MonthView only; DayView uses its own week strip) */}
            <div
              style={{
                flex: 1,
                minWidth: 0,
                display: "flex",
                justifyContent: "center",
              }}
            >
              {isMonth && (
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "0.75rem",
                  }}
                >
                  <button
                    type="button"
                    aria-label="Mes anterior"
                    onClick={goToPrevMonth}
                    style={{
                      background: "transparent",
                      border: 0,
                      color: "#6D57BC",
                      fontSize: "3rem",
                      width: "4rem",
                      height: "4rem",
                      display: "grid",
                      placeItems: "center",
                      alignContent: "center",
                      lineHeight: 1,
                      cursor: "pointer",
                    }}
                  >
                    ‹
                  </button>
                  <h3
                    style={{
                      margin: 0,
                      color: "#6D57BC",
                      fontWeight: 700,
                      whiteSpace: "nowrap",
                      fontSize: "1.25rem",
                    }}
                  >
                    {monthLabel}
                  </h3>
                  <button
                    type="button"
                    aria-label="Mes siguiente"
                    onClick={goToNextMonth}
                    style={{
                      background: "transparent",
                      border: 0,
                      color: "#6D57BC",
                      fontSize: "3rem",
                      width: "4rem",
                      height: "4rem",
                      display: "grid",
                      placeItems: "center",
                      alignContent: "center",
                      lineHeight: 1,
                      cursor: "pointer",
                    }}
                  >
                    ›
                  </button>
                </div>
              )}
            </div>

            {/* Right: búsqueda de pacientes (misma posición que en semanal) + timezone selector.
                alignItems: flex-start (no center) — el bloque de timezone tiene 2 líneas
                (select + "Huso horario: ..."), centrar contra las dos lo corre hacia abajo
                respecto del select; flex-start lo deja a la misma altura que el select. */}
            <div style={{ display: "flex", alignItems: "flex-start", gap: "0.5rem" }}>
              {!editMode.calendarEditMode && !isSchedulingMode && !isRescheduleMode && (
                <PatientAgendaSearch />
              )}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-end",
                  gap: "0.25rem",
                }}
              >
                <select
                  disabled
                  value={detectedTimezone}
                  style={{
                    padding: "0.375rem 0.625rem",
                    fontSize: "0.8125rem",
                    border: "1px solid #cbd5e1",
                    borderRadius: "0.25rem",
                    background: "#f9fafb",
                    color: "#6b7280",
                    cursor: "not-allowed",
                  }}
                >
                  <option value={detectedTimezone}>{timezoneLabel}</option>
                </select>
                <span
                  data-testid="huso-horario-label"
                  style={{
                    fontSize: "0.65rem",
                    color: "#9ca3af",
                    fontWeight: 500,
                    letterSpacing: "0.025em",
                    lineHeight: 1.3,
                    textAlign: "right",
                    whiteSpace: "nowrap",
                  }}
                >
                  Huso horario: {tzIso2 && <><CountryFlag iso2={tzIso2} size="0.65rem" /> </>}{tzCountryName}
                </span>
              </div>
            </div>
          </div>
        )}
      </>
    );
  };

  // ─── Week view ─────────────────────────────────────────────────────────────
  if (currentView === "week") {
    return (
      <section className="config-section no-padding">
        <TimezoneAgendaUpdatedModal
          open={timezoneChangeInfo !== null}
          prevTimezone={timezoneChangeInfo?.prevTz ?? null}
          newTimezone={timezoneChangeInfo?.newTz ?? null}
          onClose={handleCloseTimezoneModal}
        />
        <div
          className="calendar-actions-row"
          style={{
            position: "relative",
            display:
              editMode.specificDayDate !== null ? "none" : "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            padding: isMobile ? "0.75rem 1rem" : "1.25rem 2rem",
            flexShrink: 0,
          }}
        >
          {/* Posicionado absoluto: queda fuera del flujo del documento, así no altera la
              altura de la fila ni desplaza nada debajo (lo que desincronizaría el spotlight
              cacheado de los tours Joyride con la posición real de sus targets). */}
          {!isMobile && !editMode.calendarEditMode && !isSchedulingMode && !isRescheduleMode && (
            <div style={{ position: "absolute", left: "50%", top: "50%", transform: "translate(-50%, -50%)" }}>
              <GoogleMeetButton />
            </div>
          )}
          {/* Left slot: "Sin próxima sesión" button (idéntico al de day/month view) */}
          {patientsMissingNext.length + patientsHandled.length > 0 && (
            <button
              type="button"
              data-testid="btn-sin-proxima-sesion"
              onClick={openMissingNextModal}
              title="Ver pacientes sin próxima sesión"
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: isMobile ? 0 : "0.375rem",
                padding: isMobile ? "0" : "0.375rem 0.875rem",
                width: isMobile ? "40px" : undefined,
                borderRadius: "9999px",
                border: patientsMissingNext.length > 0
                  ? "2px solid var(--secundario-naranja, #f57e2e)"
                  : "2px solid #d1d5db",
                background: "transparent",
                color: patientsMissingNext.length > 0
                  ? "var(--secundario-naranja, #f57e2e)"
                  : "#9ca3af",
                fontSize: isMobile ? "0.875rem" : "0.8125rem",
                fontWeight: 600,
                fontFamily: "Poppins, sans-serif",
                cursor: "pointer",
                whiteSpace: "nowrap",
                flexShrink: 0,
                height: "40px",
              }}
            >
              {!isMobile && "Sin próxima sesión"}
              <span
                data-testid="badge-sin-proxima"
                style={{
                  background: patientsMissingNext.length > 0
                    ? "var(--secundario-naranja, #f57e2e)"
                    : "#d1d5db",
                  color: "#fff",
                  borderRadius: "9999px",
                  fontSize: "0.6875rem",
                  fontWeight: 700,
                  minWidth: "1.25rem",
                  height: "1.25rem",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "0 0.25rem",
                }}
              >
                {patientsMissingNext.length + patientsHandled.length}
              </span>
            </button>
          )}
          <div
            style={{
              display: "flex",
              gap: "0.5rem",
              alignItems: "center",
              justifyContent: "flex-end",
              width: "100%",
            }}
          >
            <CalendarConfigDropdown
              calendarEditMode={editMode.calendarEditMode}
              isSchedulingMode={isSchedulingMode}
              isRescheduleMode={isRescheduleMode}
              isTogglingEdit={editMode.isTogglingEdit}
              configDropdownOpen={configDropdownOpen}
              setConfigDropdownOpen={setConfigDropdownOpen}
              configDropdownRef={configDropdownRef}
              runTour={tour.runTour}
              onReplayTour={tour.replayTour}
              onScheduleSession={() =>
                setOpenScheduleModalTick(Date.now())
              }
              onStartRecurrent={handleStartRecurrent}
              onStartSpecificDay={handleStartSpecificDay}
              onStartBlock={handleStartBlock}
              onOpenHelp={() => setHelpModalOpen(true)}
              onOpenRescheduleInfo={() => openRescheduleInfoFnRef.current?.()}
              onToday={() => todayFnRef.current?.()}
            />
          </div>
        </div>

        {/* ViewSelector row — only visible on mobile in WeekView (desktop has it in Row 2) */}
        {isMobile && (
          <div
            data-testid="week-view-selector-row"
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: "0.375rem",
              padding: "0.375rem 1rem 0.5rem",
              borderBottom: "1px solid #f1f5f9",
            }}
          >
            <div style={{ display: "flex", alignItems: "center" }}>
              {viewSelectorNode}
            </div>
          </div>
        )}

        <CalendarOnboarding
          tourKey={tour.tourKey}
          steps={tour.steps}
          runTour={tour.runTour}
          stepIndex={tour.stepIndex}
          canSkipTour={tour.canSkipTour}
          onTourCallback={tour.onTourCallback}
          runBookingTour={tour.runBookingTour}
          bookingStepIndex={tour.bookingStepIndex}
          onBookingTourCallback={tour.onBookingTourCallback}
          runSpecificDayTour={tour.runSpecificDayTour}
          specificDayTourStep={tour.specificDayTourStep}
          setSpecificDayTourStep={tour.setSpecificDayTourStep}
          specificDayTourKey={tour.specificDayTourKey}
          setRunSpecificDayTour={tour.setRunSpecificDayTour}
          runBlockTour={tour.runBlockTour}
          blockTourStep={tour.blockTourStep}
          setBlockTourStep={tour.setBlockTourStep}
          blockTourKey={tour.blockTourKey}
          setRunBlockTour={tour.setRunBlockTour}
          runRescheduleTour={tour.runRescheduleTour}
          rescheduleTourStep={tour.rescheduleTourStep}
          setRescheduleTourStep={tour.setRescheduleTourStep}
          rescheduleTourKey={tour.rescheduleTourKey}
          setRescheduleTourKey={tour.setRescheduleTourKey}
          setRunRescheduleTour={tour.setRunRescheduleTour}
          runConfigureNowTour={tour.runConfigureNowTour}
          configureNowTourStep={tour.configureNowTourStep}
          setConfigureNowTourStep={tour.setConfigureNowTourStep}
          configureNowTourKey={tour.configureNowTourKey}
          setRunConfigureNowTour={tour.setRunConfigureNowTour}
        />

        <NzWelcomeModal
          open={tour.showWelcomeModal}
          onConfirm={tour.handleWelcomeModalConfirm}
        />

        <MandatoryScheduleCtaModal
          open={tour.showMandatoryCtaModal}
          onConfirm={tour.handleMandatoryCtaConfirm}
        />

        <div
          style={{
            textAlign: "left",
            flex: 1,
            minHeight: 0,
            overflow: "visible",
            padding: isMobile
              ? "0 0.5rem 1rem 0.5rem"
              : "0 2rem 1rem 2rem",
          }}
        >
          {!tour.runTour &&
            tour.stepIndex === 7 &&
            editMode.calendarEditMode && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginLeft: "auto",
                  width: "fit-content",
                  gap: "0.5rem",
                  padding: "0.625rem 1rem",
                  background: "#f5f3ff",
                  border: "1px solid #e0d9f6",
                  borderRadius: "10px",
                  marginBottom: "0.75rem",
                  fontSize: "0.8125rem",
                  color: "#5b4a9e",
                  lineHeight: 1.4,
                }}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#5b4a9e"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ flexShrink: 0 }}
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="16" x2="12" y2="12" />
                  <line x1="12" y1="8" x2="12.01" y2="8" />
                </svg>
                <span>
                  Una vez que termines de configurar tu agenda, hacé
                  click en{" "}
                  <strong>&ldquo;Guardar cambios&rdquo;</strong> para
                  continuar.
                </span>
              </div>
            )}

          <HolidayAgendaNotice className="mb-3" />

          <CalendarSchedulingBanner
            isSchedulingMode={isSchedulingMode}
            isRescheduleMode={isRescheduleMode}
            reschedulingSessionInfo={reschedulingSessionInfo}
            schedulingPatientName={schedulingPatientName}
            onCancel={() => setCancelSchedulingTick(Date.now())}
          />

          <div
            id="calendar-grid-area"
            style={isMobile ? { paddingBottom: "80px" } : undefined}
          >
            <WeeklyPsychologistCalendar
              key={session.refreshKey}
              psychologist={calendarPsychologistProp}
              onSessionViewProcess={(s: SessionWithPsychologist) => {
              session.handleCloseSessionModal();
              setCalendarDetailPatient({
                id: s.process_id || '',
                processId: s.process_id || '',
                name: s.patient.name || s.patient.email,
                service: s.specialty,
                totalSessions: 0,
              });
            }}
            editMode={editMode.calendarEditMode}
              onEditModeToggle={() =>
                editMode.setCalendarEditMode(!editMode.calendarEditMode)
              }
              forceRefresh={!editMode.calendarEditMode}
              openScheduleModalTick={openScheduleModalTick}
              openBlockModalTick={editMode.openBlockModalTick}
              onCloseBlockModal={editMode.handleCloseBlockModal}
              blockInitialDate={blockPrefill?.date}
              blockInitialReason={blockPrefill?.reason}
              openCreateModalForDate={editMode.specificDayDate}
              onCloseCreateModal={() => {
                editMode.setSpecificDayDate(null);
                editMode.setCalendarEditMode(false);
              }}
              isCancelling={session.isCancelling}
              isSessionModalOpen={session.isSessionModalOpen}
              setIsSessionModalOpen={session.setIsSessionModalOpen}
              handleCancelSession={session.handleCancelSession}
              handleCloseSessionModal={session.handleCloseSessionModal}
              selectedSession={session.selectedSession}
              setSelectedSession={session.setSelectedSession}
              onSchedulingModeChange={(isScheduling, patientName, processId) => {
                setIsSchedulingMode(isScheduling);
                setSchedulingPatientName(
                  isScheduling ? patientName : undefined,
                );
                if (isScheduling) {
                if (processId) onBookingStateChange?.(processId);
              } else if (wasSchedulingRef.current) {
                // Agendamiento terminado (confirmado o cancelado): limpiar la URL.
                onBookingEnded?.();
              }
              wasSchedulingRef.current = isScheduling;
            }}
              onRescheduleModeChange={handleRescheduleModeChange}
              cancelSchedulingTick={cancelSchedulingTick}
              preloadProcessId={preloadProcessId}
              preloadForceSelect={autoOpenSchedule}
            onPreloadConsumed={onPreloadConsumed}
              autoOpenPatientEmail={autoOpenPatientEmail}
              autoOpenDate={autoOpenDate}
              onAutoOpenConsumed={onAutoOpenConsumed}
              autoOpenSessionId={autoOpenSessionId}
            autoOpenSessionView={autoOpenSessionView}
            onAutoOpenSessionConsumed={onAutoOpenSessionConsumed}
            onScheduleHasChangesChange={
                editMode.handleScheduleHasChangesChange
              }
              isExitingEdit={editMode.isExitingEdit}
              onSpecificDayHasChangesChange={
                editMode.setSpecificDayHasChanges
              }
              onBlockHasChangesChange={editMode.setBlockHasChanges}
              isOnboardingMockMode={tour.isOnboardingMockMode}
              isTourRunning={tour.runTour || tour.runBookingTour}
              todayFnRef={todayFnRef}
              viewSelector={viewSelectorNode}
              openRescheduleInfoFnRef={openRescheduleInfoFnRef}
              openRescheduleSummaryFnRef={openRescheduleSummaryFnRef}
              onOpenAgendaHelp={() => setHelpModalOpen(true)}
          />
          </div>
        </div>

        {rescheduleBanner}

        <LargeLoading
          open={editMode.isTogglingEdit}
          message="Cargando configuración"
        />

        <CalendarUnsavedModal
          open={editMode.showUnsavedModal}
          onKeepEditing={() => {
            editMode.setShowUnsavedModal(false);
            editMode.setPendingCloseCallback(null);
          }}
          onDiscard={handleDiscard}
        />

        <CalendarHelpModal
          open={helpModalOpen}
          onClose={() => setHelpModalOpen(false)}
        />

        {missingNextModalOpen && (
          <MissingNextSessionModal
            patients={patientsMissingNext}
            patientsHandled={patientsHandled}
            onSchedule={handleMissingNextSchedule}
            onNoSchedule={handleMissingNextNoSchedule}
            onDismiss={dismissMissingNextModal}
            onViewPatient={(p) => {
              dismissMissingNextModal();
              if (p.patientId) {
                handleDayViewPatientClick(p.patientId, p.processId, p.patientName);
              }
            }}
          />
        )}

        {calendarDetailPatient && (
          <PatientDetailModal
            patient={calendarDetailPatient}
            onClose={() => setCalendarDetailPatient(null)}
          />
        )}

        <NoAgendamientoModal params={noAgendamientoModal.params} onClose={noAgendamientoModal.close} />
      </section>
    );
  }

  // ─── Month / Day view ─────────────────────────────────────────────────────
  return (
    <section className="config-section no-padding">
      <TimezoneAgendaUpdatedModal
          open={timezoneChangeInfo !== null}
          prevTimezone={timezoneChangeInfo?.prevTz ?? null}
          newTimezone={timezoneChangeInfo?.newTz ?? null}
          onClose={handleCloseTimezoneModal}
        />
      {renderPeriodHeader()}

      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflow: "visible",
          padding: isMobile ? "0 0.5rem 1rem 0.5rem" : "0 2rem 1rem 2rem",
        }}
      >
        {currentView === "month" ? (
          <MonthView
            currentMonthDate={currentMonthDate}
            onNavigateToDay={(iso) => {
              const next = new URLSearchParams(searchParams);
              next.set("date", iso);
              navigate(`${calendarPath("day")}?${next.toString()}`, { replace: true });
            }}
            onSessionClick={handleMonthSessionClick}
            isMobile={isMobile}
            onSwipeNext={goToNextMonth}
            onSwipePrev={goToPrevMonth}
            therapistTimezone={therapistTz}
          />
        ) : (
          <DayView
            psychologist={psychologist}
            onPatientClick={handleDayViewPatientClick}
            onSchedule={handleScheduleFromOtherView}
            onOpenSessionModal={handleMonthSessionClick}
            goToTodayRef={dayViewTodayRef}
          />
        )}
      </div>

      {rescheduleBanner}

      {dayViewPatient && (
        <PatientDetailModal
          patient={dayViewPatient}
          onClose={() => setDayViewPatient(null)}
        />
      )}

      {missingNextModalOpen && (
        <MissingNextSessionModal
          patients={patientsMissingNext}
          patientsHandled={patientsHandled}
          onSchedule={handleMissingNextSchedule}
          onNoSchedule={handleMissingNextNoSchedule}
          onDismiss={dismissMissingNextModal}
          onViewPatient={(p) => {
            dismissMissingNextModal();
            if (p.patientId) {
              handleDayViewPatientClick(p.patientId, p.processId, p.patientName);
            }
          }}
        />
      )}

      {session.isSessionModalOpen && session.selectedSession && (
        <SessionDetailsModal
          key={session.selectedSession.id}
          open={session.isSessionModalOpen}
          onClose={session.handleCloseSessionModal}
          session={session.selectedSession}
          onCancel={session.handleCancelSession}
          psychologistId={psychologist?.id ?? undefined}
          therapistTimezone={therapistTz}
          onViewProcess={() => {
            const s = session.selectedSession!;
            session.handleCloseSessionModal();
            setCalendarDetailPatient({
              id: s.process_id || '',
              processId: s.process_id || '',
              name: s.patient.name || s.patient.email,
              service: s.specialty,
              totalSessions: 0,
            });
          }}
          onStartReschedule={() => {
            session.handleCloseSessionModal();
            handleScheduleFromOtherView();
          }}
        />
      )}

      <CalendarHelpModal
        open={helpModalOpen}
        onClose={() => setHelpModalOpen(false)}
      />

      {calendarDetailPatient && (
        <PatientDetailModal
          patient={calendarDetailPatient}
          onClose={() => setCalendarDetailPatient(null)}
        />
      )}

      <Dialog open={sesionesRedirectModalOpen} onOpenChange={(next) => { if (!next) closeSesionesRedirectModal(); }}>
        <DialogContent className="p-6 sm:max-w-[480px]" data-testid="sesiones-redirect-modal">
          <div className="flex flex-col gap-4">
            <DialogTitle className="text-xl font-bold" style={{ color: 'var(--textos-azul, #1e3a5f)' }}>
              ✨ La vista &quot;Sesiones&quot; se integra a tu Agenda
            </DialogTitle>
            <DialogDescription className="text-base leading-relaxed" style={{ color: 'var(--textos-gris, #6b7280)' }}>
              Para simplificar tu flujo de trabajo, este acceso desde el menú dejará de estar disponible próximamente.
              <br /><br />
              Todo lo que hoy ves aquí lo encontrarás en <strong>Agenda → Vista del Día</strong>, con novedades incluidas.
            </DialogDescription>
            <div className="flex justify-end pt-2">
              <Button onClick={closeSesionesRedirectModal} data-testid="sesiones-redirect-modal-cta">
                Entendido
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {showAcuityReview && (
        <Dialog
          open
          onOpenChange={(next) => { if (!next) acuityMigration.dismissReview().catch(() => { /* noop */ }); }}
        >
          <DialogContent className="p-6 sm:max-w-md">
            <div className="flex flex-col text-left gap-4">
              <DialogTitle className="text-xl">Revisá tus horarios migrados</DialogTitle>
              <DialogDescription className="text-base">
                Estos son los horarios que trajimos desde Acuity. Verificá que estén correctos
                o configuralos como prefieras.
              </DialogDescription>
              <Button
                onClick={() => acuityMigration.dismissReview().catch(() => { /* noop */ })}
                className="self-end"
              >
                Entendido
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      <NoAgendamientoModal params={noAgendamientoModal.params} onClose={noAgendamientoModal.close} />
    </section>
  );
};
